// pages/reports/PivotInfoReport.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material';
import {
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Analytics as Icon,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';

import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, PivotGroup } from '../../services/reportService';
import { tradesService } from '../../services/tradesService';
import { useAuth } from '../../contexts/AuthContext';
import { fmt2, fmtDate } from '../../components/reports/ReportLayout';
import { fmtBD } from '../../components/common/bdNumber';

const ACCENT = '#667EEA';

// 600 -> "600", 599.5 -> "599.5"
const fmtLitre = (v: number | null | undefined): string => {
  const n = Number(v ?? 0);
  return Number.isInteger(n) ? fmtBD(n, 0) : fmtBD(n, 1);
};

// ─────────────────────────────────────────────────────────────
// Permission helper
// ─────────────────────────────────────────────────────────────
const useIsDataManager = () => {
  const { user } = useAuth();
  return user?.is_superuser || user?.role?.role_name === 'DATA_MANAGER';
};

// ─────────────────────────────────────────────────────────────
// Months & Years
// ─────────────────────────────────────────────────────────────
const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const RANKS = [
  { value: 'A', label: 'A' }, { value: 'B', label: 'B' },
  { value: 'C', label: 'C' }, { value: 'D', label: 'D' },
  { value: 'N', label: 'N' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i),
}));

// ─────────────────────────────────────────────────────────────
// Styled Header Cell
// ─────────────────────────────────────────────────────────────
const HCell: React.FC<{ label: string; align?: 'left' | 'right' | 'center'; first?: boolean; last?: boolean }> = ({
  label,
  align = 'right',
  first,
  last,
}) => (
  <TableCell
    sx={{
      fontWeight: 700,
      fontSize: '0.72rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#fff',
      py: 1.4,
      px: 1.5,
      whiteSpace: 'nowrap',
      textAlign: align,
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: `linear-gradient(135deg, ${ACCENT} 0%, #764ba2 100%)`,
      borderRight: last ? 'none' : '1px solid rgba(255,255,255,0.15)',
      borderBottom: 'none',
      ...(first && { borderTopLeftRadius: 0 }),
      ...(last && { borderTopRightRadius: 0 }),
    }}
  >
    {label}
  </TableCell>
);

// ─────────────────────────────────────────────────────────────
// Body Cell Helper
// ─────────────────────────────────────────────────────────────
const cellSx = (align: 'left' | 'right' | 'center' = 'left', extra?: object) => ({
  py: 1.1,
  px: 1.5,
  fontSize: '0.8rem',
  textAlign: align,
  borderBottom: `1px solid ${alpha(ACCENT, 0.07)}`,
  borderRight: `1px solid ${alpha(ACCENT, 0.05)}`,
  ...extra,
});

// ═════════════════════════════════════════════════════════════
// Pivot Information Report
// ═════════════════════════════════════════════════════════════
const PivotInfoReport: React.FC = () => {
  const isDataManager = useIsDataManager();

  // ───────────────────────────────────────────────────────────
  // Data & Filter State
  // ───────────────────────────────────────────────────────────
  const [pivot, setPivot] = useState<Record<string, PivotGroup>>({});
  const [grand, setGrand] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [dlLoading, setDlLoading] = useState<'excel' | 'pdf' | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [products, setProducts] = useState<any[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);

  const [filters, setFilters] = useState<Record<string, string>>({
    year: '',
    month: '',
    date_from: '',
    date_to: '',
    product_id: '',
    seller_info_id: '',
    seller_rank: '',
  });

    // Seller dropdown narrowed to the selected rank
    const sellerOptions = filters.seller_rank
    ? sellers.filter(s => s.rank === filters.seller_rank)
    : sellers;

  // ───────────────────────────────────────────────────────────
  // Vertical Scroll Arrow Rail
  // ───────────────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const holdDelay = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const [scroll, setScroll] = useState({ up: false, down: false, overflow: false });

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight > 2;
    setScroll({
      overflow,
      up: el.scrollTop > 2,
      down: overflow && el.scrollTop < el.scrollHeight - el.clientHeight - 2,
    });
  }, []);

  const scrollByStep = (dy: number, smooth = true) => {
    scrollRef.current?.scrollBy({ top: dy, behavior: smooth ? 'smooth' : 'auto' });
  };

  const stopHold = useCallback(() => {
    if (holdDelay.current) {
      clearTimeout(holdDelay.current);
      holdDelay.current = null;
    }
    if (holdTimer.current) {
      clearInterval(holdTimer.current);
      holdTimer.current = null;
    }
  }, []);

  const startHold = (dir: 1 | -1) => {
    stopHold();
    scrollByStep(dir * 140);
    holdDelay.current = setTimeout(() => {
      holdTimer.current = setInterval(() => {
        scrollByStep(dir * 40, false);
      }, 30);
    }, 300);
  };

  const arrowKey = (dir: 1 | -1) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollByStep(dir * 160);
    }
  };

  const arrowBtnSx = {
    borderRadius: 0,
    color: ACCENT,
    '&:hover': { background: alpha(ACCENT, 0.14) },
    '&.Mui-disabled': { color: alpha(ACCENT, 0.25) },
  } as const;

  // ───────────────────────────────────────────────────────────
  // Load Product / Seller Dropdowns
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    tradesService.listProducts({ is_active: true })
      .then(r => setProducts(r.results.map(p => ({ value: p.product_id, label: p.product_name }))))
      .catch(() => {});

    tradesService.listSellers()
      .then(r => setSellers(r.results.map(s => ({
        value: s.seller_info_id, label: s.seller_name, rank: s.seller_rank,
      }))))
      .catch(() => {});
  }, []);

  // ───────────────────────────────────────────────────────────
  // Fetch Pivot Data
  // ───────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) p[k] = v;
      });

      const res = await reportService.getPivotReport(p);
      setPivot(res.pivot || {});
      setGrand(res.grand_total || {});

      const exp: Record<string, boolean> = {};
      Object.keys(res.pivot || {}).forEach(k => {
        exp[k] = false;
      });
      setExpanded(exp);
    } catch {
      // Existing behaviour
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ───────────────────────────────────────────────────────────
  // Recalculate arrow availability when table/data changes
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    updateScroll();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScroll, pivot, loading, expanded]);

  useEffect(() => () => stopHold(), [stopHold]);

  // ───────────────────────────────────────────────────────────
  // Download
  // ───────────────────────────────────────────────────────────
  const handleDownload = async (fmt: 'excel' | 'pdf') => {
    setDlLoading(fmt);
    try {
      const p: any = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) p[k] = v;
      });
      await reportService.downloadPivot(fmt, p);
    } finally {
      setDlLoading(null);
    }
  };

  // ───────────────────────────────────────────────────────────
  // Expand / Collapse Seller
  // ───────────────────────────────────────────────────────────
  const toggle = (name: string) => {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const sellerEntries = Object.entries(pivot).sort(([a], [b]) => a.localeCompare(b));

  // ───────────────────────────────────────────────────────────
  // Summary Cards
  // ───────────────────────────────────────────────────────────
  const cards = [
    { label: 'Total Sellers', value: sellerEntries.length, color: ACCENT },
    { label: 'Total Quantity', value: `${fmt2(grand.total_pcs ?? 0)} Pcs \n${fmtLitre(grand.total_litre)} L`, color: '#2196F3' },
    { label: 'Total Purchases (Tk.)', value: fmt2(grand.total_purchase ?? 0), color: '#00BCD4' },
    { label: 'Total Sales (Tk.)', value: fmt2(grand.total_sales ?? 0), color: '#4CAF50' },
    { label: 'Total Cash (Tk.)', value: fmt2(grand.total_cash ?? 0), color: '#FF9800' },
    { label: 'Total Due (Tk.)', value: fmt2(grand.total_due ?? 0), color: '#f44336' },
    { label: 'Profit / Loss (Tk.)', value: fmt2(grand.total_profit ?? 0), color: '#667EEA' },
  ];

  // ═══════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════
  return (
    <Fade in timeout={400}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                background: `linear-gradient(135deg, ${ACCENT} 0%, #764ba2 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Pivot Report
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Sales summary grouped by seller and product
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchData}
                size="small"
                sx={{ border: `1px solid ${alpha(ACCENT, 0.3)}`, color: ACCENT }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {isDataManager && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={dlLoading === 'excel' ? <CircularProgress size={14} /> : <ExcelIcon />}
                  onClick={() => handleDownload('excel')}
                  disabled={!!dlLoading || loading}
                  sx={{ borderRadius: 2, borderColor: '#4CAF50', color: '#4CAF50', '&:hover': { background: alpha('#4CAF50', 0.06) } }}
                >
                  Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={dlLoading === 'pdf' ? <CircularProgress size={14} /> : <PdfIcon />}
                  onClick={() => handleDownload('pdf')}
                  disabled={!!dlLoading || loading}
                  sx={{ borderRadius: 2, borderColor: '#f44336', color: '#f44336', '&:hover': { background: alpha('#f44336', 0.06) } }}
                >
                  PDF
                </Button>
              </>
            )}
          </Stack>
        </Box>

        {/* Summary Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
          {cards.map(c => (
            <Card
              key={c.label}
              elevation={0}
              sx={{
                flex: 1,
                minWidth: 130,
                p: 1.75,
                borderRadius: 3,
                textAlign: 'center',
                whiteSpace: 'pre-line',
                border: `1px solid ${alpha(c.color, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(c.color, 0.07)} 0%, ${alpha(c.color, 0.02)} 100%)`,
                transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 20px ${alpha(c.color, 0.18)}` },
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ letterSpacing: '0.03em' }}>
                {c.label}
              </Typography>
              <Typography fontWeight={800} sx={{ color: c.color, lineHeight: 1.2, fontSize: '0.95rem', mt: 0.25, textAlign: 'center' }}>
                {c.value}
              </Typography>
            </Card>
          ))}
        </Stack>

        {/* Filters */}
        <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}` }}>
          <ReportFilterBar
            accentColor={ACCENT}
            values={filters}
            onChange={(k, v) => setFilters(prev => {
              const next = { ...prev, [k]: v };
              // changing rank clears a seller that no longer matches it
              if (k === 'seller_rank' && v && prev.seller_info_id) {
                const sel = sellers.find(s => String(s.value) === String(prev.seller_info_id));
                if (sel && sel.rank !== v) next.seller_info_id = '';
              }
              return next;
            })}
            onClear={() => setFilters({ year: '', month: '', date_from: '', date_to: '', product_id: '', seller_info_id: '', seller_rank: '' })}
            fields={[
              { type: 'select', key: 'year', label: 'Year', options: YEARS, minWidth: 100 },
              { type: 'select', key: 'month', label: 'Month', options: MONTHS, minWidth: 100 },
              { type: 'date', key: 'date_from', label: 'From' },
              { type: 'date', key: 'date_to', label: 'To' },
              { type: 'select', key: 'product_id', label: 'Product', options: products, minWidth: 200 },
              { type: 'select', key: 'seller_info_id', label: 'Seller', options: sellers, minWidth: 180 },
              { type: 'select', key: 'seller_rank',    label: 'Rank',   options: RANKS,         minWidth: 90 },
            ]}
          />
        </Card>

        {/* Pivot Table Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(ACCENT, 0.12)}`,
            overflow: 'hidden', // important so arrow rail / table remain inside card
          }}
        >
          {/* Top Bar */}
          <Box sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${alpha(ACCENT, 0.08)}`, background: alpha(ACCENT, 0.03) }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: ACCENT, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Pivot Data
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {sellerEntries.length} seller{sellerEntries.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Scrollable Table + Arrow Rail */}
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Main Scrollable Grid */}
            <TableContainer
              ref={scrollRef}
              onScroll={updateScroll}
              sx={{
                flex: 1,
                minWidth: 0,
                maxHeight: 'calc(100vh - 340px)',
                overflowX: 'auto',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 6, height: 6 },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: 10 },
                '&::-webkit-scrollbar-thumb': { background: alpha(ACCENT, 0.4), borderRadius: 10 },
                '&::-webkit-scrollbar-thumb:hover': { background: alpha(ACCENT, 0.7) },
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 750, borderCollapse: 'separate', borderSpacing: 0 }}>
                {/* Table Header */}
                <TableHead>
                  <TableRow>
                    <HCell label="#" align="center" first />
                    <HCell label="Date" align="center" />
                    <HCell label="Seller / Product" align="center" />
                    <HCell label="Quantity" align="center" />
                    <HCell label="Total Sales (Tk.)" align="center" />
                    <HCell label="Cash (Tk.)" align="center" />
                    <HCell label="Due (Tk.)" align="center" />
                    <HCell label="Profit/Loss (Tk.)" align="center" last />
                  </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, border: 'none' }}>
                        <CircularProgress size={32} sx={{ color: ACCENT }} />
                        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                          Loading data…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : sellerEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8, border: 'none' }}>
                        <Icon sx={{ fontSize: 44, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1.5 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          No pivot data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {/* Seller Groups */}
                      {sellerEntries.map(([sellerName, grp], si) => (
                        <React.Fragment key={sellerName}>
                          {/* Seller Header */}
                          <TableRow
                            onClick={() => toggle(sellerName)}
                            sx={{
                              cursor: 'pointer',
                              background: `linear-gradient(90deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(ACCENT, 0.04)} 100%)`,
                              transition: 'background 0.15s',
                              '&:hover': { background: alpha(ACCENT, 0.14) },
                              '& td:first-of-type': { boxShadow: `inset 3px 0 0 ${ACCENT}` },
                            }}
                          >
                            <TableCell sx={{ ...cellSx('center'), fontWeight: 700, color: ACCENT, fontSize: '0.82rem', minWidth: 48 }}>
                              {si + 1}
                            </TableCell>
                            <TableCell sx={{ ...cellSx('left'), color: 'text.disabled', fontSize: '0.78rem', minWidth: 110 }}>—</TableCell>
                            <TableCell sx={{ ...cellSx('left'), fontWeight: 800, fontSize: '0.875rem', minWidth: 200 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 22, height: 22, borderRadius: '6px', background: alpha(ACCENT, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', color: ACCENT, flexShrink: 0 }}>
                                  {expanded[sellerName] ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
                                </Box>
                                {sellerName}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#2196F3', minWidth: 70 }}>
                              {fmt2(grp.total_pcs)} Pcs / {fmtLitre(grp.total_litre)} L
                            </TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#4CAF50', minWidth: 130 }}>{fmt2(grp.total_sales)}</TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#FF9800', minWidth: 120 }}>{fmt2(grp.total_cash)}</TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#f44336', minWidth: 110 }}>{fmt2(grp.total_due)}</TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#9C27B0', minWidth: 120, borderRight: 'none' }}>{fmt2(grp.total_profit)}</TableCell>
                          </TableRow>

                          {/* Product Rows */}
                          {expanded[sellerName] && grp.rows.map((r, i) => (
                            <TableRow
                              key={i}
                              sx={{
                                background: i % 2 === 0 ? '#ffffff' : alpha(ACCENT, 0.018),
                                transition: 'background 0.15s',
                                '&:hover': { background: alpha(ACCENT, 0.07), '& td': { color: 'text.primary' } },
                              }}
                            >
                              <TableCell sx={{ ...cellSx('center'), color: 'text.disabled', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                              <TableCell sx={{ ...cellSx('left'), color: 'text.secondary', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {r.sale_date ? fmtDate(r.sale_date) : '—'}
                              </TableCell>
                              <TableCell sx={{ ...cellSx('left'), color: 'text.secondary', pl: 4 }}>{r.product_name}</TableCell>
                              <TableCell sx={{ ...cellSx('right'), fontWeight: 600, color: '#2196F3' }}>{fmt2(r.quantity_pcs)}</TableCell>
                              <TableCell sx={{ ...cellSx('right') }}>{fmt2(r.total_sales_price)}</TableCell>
                              <TableCell sx={{ ...cellSx('right'), color: '#FF9800' }}>{fmt2(r.cash_amount)}</TableCell>
                              <TableCell sx={{ ...cellSx('right'), color: '#f44336' }}>{fmt2(r.due_amount)}</TableCell>
                              <TableCell
                                sx={{
                                  ...cellSx('right'),
                                  color: Number(r.total_profit) >= 0 ? '#4CAF50' : '#f44336',
                                  borderRight: 'none',
                                }}
                              >
                                {fmt2(r.total_profit)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}

                      {/* Grand Total */}
                      <TableRow
                        sx={{
                          background: `linear-gradient(90deg, ${alpha(ACCENT, 0.14)} 0%, ${alpha(ACCENT, 0.06)} 100%)`,
                          '& td:first-of-type': { boxShadow: `inset 4px 0 0 ${ACCENT}` },
                        }}
                      >
                        <TableCell sx={{ ...cellSx('center') }} />
                        <TableCell sx={{ ...cellSx('left') }} />
                        <TableCell sx={{ ...cellSx('left'), fontWeight: 800, fontSize: '0.875rem', color: ACCENT, letterSpacing: '0.04em' }}>
                          GRAND TOTAL
                        </TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#2196F3' }}>
                          {fmt2(grand.total_pcs ?? 0)} Pcs / {fmtLitre(grand.total_litre)} L
                        </TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#4CAF50' }}>{fmt2(grand.total_sales ?? 0)}</TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#FF9800' }}>{fmt2(grand.total_cash ?? 0)}</TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#f44336' }}>{fmt2(grand.total_due ?? 0)}</TableCell>
                        <TableCell
                          sx={{
                            ...cellSx('right'),
                            fontWeight: 800,
                            color: Number(grand.total_profit ?? 0) >= 0 ? '#4CAF50' : '#f44336',
                            borderRight: 'none',
                          }}
                        >
                          {fmt2(grand.total_profit ?? 0)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Vertical Scroll Arrow Rail */}
            {scroll.overflow && (
              <Stack sx={{ width: 30, flexShrink: 0, borderLeft: `1px solid ${alpha(ACCENT, 0.12)}`, background: alpha(ACCENT, 0.03) }}>
                <Tooltip title="Scroll up" placement="left">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!scroll.up}
                      aria-label="Scroll up"
                      onKeyDown={arrowKey(-1)}
                      onMouseDown={() => startHold(-1)}
                      onMouseUp={stopHold}
                      onMouseLeave={stopHold}
                      onTouchStart={() => startHold(-1)}
                      onTouchEnd={stopHold}
                      sx={arrowBtnSx}
                    >
                      <KeyboardArrowUp fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box sx={{ flex: 1 }} /> {/* pushes down arrow to bottom */}

                <Tooltip title="Scroll down" placement="left">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!scroll.down}
                      aria-label="Scroll down"
                      onKeyDown={arrowKey(1)}
                      onMouseDown={() => startHold(1)}
                      onMouseUp={stopHold}
                      onMouseLeave={stopHold}
                      onTouchStart={() => startHold(1)}
                      onTouchEnd={stopHold}
                      sx={arrowBtnSx}
                    >
                      <KeyboardArrowDown fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Box>
        </Card>
      </Box>
    </Fade>
  );
};

export default PivotInfoReport;